/**
 * services/plan-fulfillment.service.ts - Služby pro analýzu plnění plánu
 *
 * FUNKCE:
 * - Souhrnná statistika plnění plánu (% plnění, dny pod plánem, celkový rozdíl kusů)
 * - Denní trendy plnění plánu
 * - Plnění podle pracovišť, projektů, směnových týmů a typů směn
 * - Distribuce plnění (histogram)
 * - Top 10 nejlepších/nejhorších výkonů
 *
 * ZDROJE DAT:
 * - Shifts (okCount, planCount) - základní data o plánované a skutečné produkci
 *
 * KLÍČOVÉ METRIKY:
 * - Fulfillment % (plnění plánu) = (okCount / planCount) × 100
 * - Piece Difference (rozdíl kusů) = planCount - okCount
 * - Days Under Plan (dny pod plánem) = počet dnů, kdy fulfillment < 100%
 *
 * VÝROBNÍ DNY:
 * Všechny denní agregace používají productionDayKey(), takže respektují výrobní den
 * definovaný v TenantModel.dayStart.
 * 
 * OPTIMALIZACE:
 * - getAllPlanFulfillmentAnalytics() - hlavní funkce, která načte data jen 2x (current + previous)
 *   a pak provede všechny analýzy v paměti bez dalších GraphQL volání
 */

import moment from 'moment-timezone';
import { ShiftsRepository } from '@/libs/tpm-analytics/data/shifts.repository';
import { productionDayKey } from '@/libs/tpm-analytics/core/production-day.utils';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';
import {
    PlanFulfillmentSummary,
    PlanFulfillmentTrendPoint,
    PlanFulfillmentByWorkplace,
    PlanFulfillmentTrendByWorkplace,
    PlanFulfillmentByProject,
    PlanFulfillmentByShiftTeam,
    PlanFulfillmentByShiftType,
    PlanFulfillmentDistribution,
    PlanFulfillmentTopItem,
} from '@/libs/tpm-analytics/core/types';

/**
 * Komplexní výsledek všech analýz plnění plánu
 */
export interface AllPlanFulfillmentAnalytics {
    summary: PlanFulfillmentSummary;
    trend: PlanFulfillmentTrendPoint[];
    byWorkplace: PlanFulfillmentByWorkplace[];
    workplaceTrend: PlanFulfillmentTrendByWorkplace[];
    byProject: PlanFulfillmentByProject[];
    byShiftTeam: PlanFulfillmentByShiftTeam[];
    byShiftType: PlanFulfillmentByShiftType[];
    distribution: PlanFulfillmentDistribution;
    topItems: {
        best: PlanFulfillmentTopItem[];
        worst: PlanFulfillmentTopItem[];
    };
}

/**
 * HLAVNÍ OPTIMALIZOVANÁ FUNKCE - načte směny pouze 2x a provede všechny analýzy
 * 
 * @param fromIso Začátek období
 * @param toIso Konec období
 * @param granularity Granularita pro trendy
 * @param filters Filtry
 * @returns Všechny analýzy plnění plánu najednou
 */
export async function getAllPlanFulfillmentAnalytics(
    fromIso: string,
    toIso: string,
    granularity: 'day' | 'week' | 'month',
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<AllPlanFulfillmentAnalytics> {
    const from = moment(fromIso);
    const to = moment(toIso);
    const span = Math.max(1, to.diff(from, 'minutes'));
    const prevTo = from.clone();
    const prevFrom = from.clone().add(-span, 'minutes');

    // POUZE 2 GraphQL VOLÁNÍ
    const [shiftsNow, shiftsPrev] = await Promise.all([
        ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters),
        ShiftsRepository.fetchShiftsForRange([], prevFrom.toISOString(), prevTo.toISOString(), filters),
    ]);

    // Všechny následující funkce pracují s již načtenými daty
    const summary = computePlanFulfillmentSummary(shiftsNow, shiftsPrev, from, to);
    const trend = computePlanFulfillmentTrend(shiftsNow, granularity);
    const byWorkplace = computePlanFulfillmentByWorkplace(shiftsNow, from, to);
    const workplaceTrend = computePlanFulfillmentTrendByWorkplace(shiftsNow, granularity);
    const byProject = computePlanFulfillmentByProject(shiftsNow, granularity);
    const byShiftTeam = computePlanFulfillmentByShiftTeam(shiftsNow, granularity);
    const byShiftType = computePlanFulfillmentByShiftType(shiftsNow, granularity);
    const distribution = computePlanFulfillmentDistribution(shiftsNow, from, to);
    const topItems = computePlanFulfillmentTopItems(shiftsNow);

    return {
        summary,
        trend,
        byWorkplace,
        workplaceTrend,
        byProject,
        byShiftTeam,
        byShiftType,
        distribution,
        topItems,
    };
}

/**
 * INTERNÍ FUNKCE - Výpočet shrnutí plnění plánu z již načtených dat
 */
function computePlanFulfillmentSummary(
    shiftsNow: any[],
    shiftsPrev: any[],
    from: moment.Moment,
    to: moment.Moment
): PlanFulfillmentSummary {
    // Agregace podle výrobních dnů
    const aggregateByDay = (shifts: any[]) => {
        const dayMap = new Map<string, { okCount: number; planCount: number }>();
        for (const s of shifts) {
            const ok = Number(s.okCount ?? 0) || 0;
            const plan = Number(s.planCount ?? 0) || 0;
            const fromDate = s.fromDate ? new Date(s.fromDate).getTime() : Date.now();
            const day = productionDayKey(fromDate);
            if (!dayMap.has(day)) {
                dayMap.set(day, { okCount: 0, planCount: 0 });
            }
            const entry = dayMap.get(day)!;
            entry.okCount += ok;
            entry.planCount += plan;
        }
        return dayMap;
    };

    const curDays = aggregateByDay(shiftsNow);
    const prevDays = aggregateByDay(shiftsPrev);

    // Celkové součty
    const curTotals = Array.from(curDays.values()).reduce(
        (acc, v) => ({ okCount: acc.okCount + v.okCount, planCount: acc.planCount + v.planCount }),
        { okCount: 0, planCount: 0 }
    );
    const prevTotals = Array.from(prevDays.values()).reduce(
        (acc, v) => ({ okCount: acc.okCount + v.okCount, planCount: acc.planCount + v.planCount }),
        { okCount: 0, planCount: 0 }
    );

    // Celkové plnění
    const fulfillmentPct = curTotals.planCount > 0 ? (curTotals.okCount / curTotals.planCount) * 100 : null;
    const prevFulfillmentPct = prevTotals.planCount > 0 ? (prevTotals.okCount / prevTotals.planCount) * 100 : null;

    // Průměrné denní plnění
    const dailyFulfillments = Array.from(curDays.values())
        .filter((v) => v.planCount > 0)
        .map((v) => (v.okCount / v.planCount) * 100);
    const avgDailyFulfillmentPct =
        dailyFulfillments.length > 0 ? dailyFulfillments.reduce((sum, v) => sum + v, 0) / dailyFulfillments.length : null;

    const prevDailyFulfillments = Array.from(prevDays.values())
        .filter((v) => v.planCount > 0)
        .map((v) => (v.okCount / v.planCount) * 100);
    const prevAvgDailyFulfillmentPct =
        prevDailyFulfillments.length > 0
            ? prevDailyFulfillments.reduce((sum, v) => sum + v, 0) / prevDailyFulfillments.length
            : null;

    // Dny pod plánem (< 100%)
    const daysUnderPlan = dailyFulfillments.filter((v) => v < 100).length;
    const prevDaysUnderPlan = prevDailyFulfillments.filter((v) => v < 100).length;

    // Celkový rozdíl kusů (okCount - planCount)
    // Kladné číslo = přebytek (nad plánem), záporné číslo = deficit (pod plánem)
    const totalPieceDiff = curTotals.okCount - curTotals.planCount;
    const prevTotalPieceDiff = prevTotals.okCount - prevTotals.planCount;

    return {
        fulfillmentPct,
        avgDailyFulfillmentPct,
        daysUnderPlan,
        totalDays: curDays.size,
        totalPieceDiff,
        prev: {
            fulfillmentPct: prevFulfillmentPct,
            avgDailyFulfillmentPct: prevAvgDailyFulfillmentPct,
            daysUnderPlan: prevDaysUnderPlan,
            totalPieceDiff: prevTotalPieceDiff,
        },
    };
}

/**
 * INTERNÍ FUNKCE - Výpočet trendu plnění plánu z již načtených dat
 */
function computePlanFulfillmentTrend(
    shifts: any[],
    granularity: 'day' | 'week' | 'month'
): PlanFulfillmentTrendPoint[] {

    const dayMap = new Map<string, { okCount: number; planCount: number }>();
    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        const plan = Number(s.planCount ?? 0) || 0;

        let key: string;
        const m = moment(s.fromDate);
        if (granularity === 'week') {
            key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
        } else if (granularity === 'month') {
            key = m.clone().startOf('month').format('YYYY-MM-DD');
        } else {
            const fromDate = s.fromDate ? new Date(s.fromDate).getTime() : Date.now();
            key = productionDayKey(fromDate);
        }

        if (!dayMap.has(key)) {
            dayMap.set(key, { okCount: 0, planCount: 0 });
        }
        const entry = dayMap.get(key)!;
        entry.okCount += ok;
        entry.planCount += plan;
    }

    return Array.from(dayMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([day, data]) => ({
            day,
            fulfillmentPct: data.planCount > 0 ? (data.okCount / data.planCount) * 100 : null,
            pieceDiff: data.okCount - data.planCount,
            planCount: data.planCount,
            okCount: data.okCount,
        }));
}

/**
 * INTERNÍ FUNKCE - Výpočet plnění plánu podle pracovišť z již načtených dat
 */
function computePlanFulfillmentByWorkplace(
    shifts: any[],
    from: moment.Moment,
    to: moment.Moment
): PlanFulfillmentByWorkplace[] {

    const wpMap = new Map<string, { name: string; okCount: number; planCount: number }>();
    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        const plan = Number(s.planCount ?? 0) || 0;
        const wpId = s.workplaceId || 'unknown';
        const wpName = (s as any)?.workplace?.name || s.workplaceId || 'N/A';

        if (!wpMap.has(wpId)) {
            wpMap.set(wpId, { name: wpName, okCount: 0, planCount: 0 });
        }
        const entry = wpMap.get(wpId)!;
        entry.okCount += ok;
        entry.planCount += plan;
    }

    return Array.from(wpMap.entries())
        .map(([workplaceId, data]) => ({
            workplaceId,
            workplaceName: data.name,
            fulfillmentPct: data.planCount > 0 ? (data.okCount / data.planCount) * 100 : null,
            planCount: data.planCount,
            okCount: data.okCount,
            pieceDiff: data.okCount - data.planCount,
        }))
        .sort((a, b) => (a.fulfillmentPct ?? 0) - (b.fulfillmentPct ?? 0)); // Od nejhoršího k nejlepšímu
}

/**
 * INTERNÍ FUNKCE - Výpočet plnění plánu podle pracovišť v čase z již načtených dat (pro trend graf)
 */
function computePlanFulfillmentTrendByWorkplace(
    shifts: any[],
    granularity: 'day' | 'week' | 'month'
): PlanFulfillmentTrendByWorkplace[] {
    // workplaceId -> day -> { okCount, planCount }
    const wpMap = new Map<string, { name: string; dayMap: Map<string, { okCount: number; planCount: number }> }>();

    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        const plan = Number(s.planCount ?? 0) || 0;
        if (ok <= 0 && plan <= 0) continue;
        
        const wpId = s.workplaceId || 'unknown';
        const wpName = (s as any)?.workplace?.name || s.workplaceId || 'N/A';

        let key: string;
        const m = moment(s.fromDate);
        if (granularity === 'week') {
            key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
        } else if (granularity === 'month') {
            key = m.clone().startOf('month').format('YYYY-MM-DD');
        } else {
            const fromDate = s.fromDate ? new Date(s.fromDate).getTime() : Date.now();
            key = productionDayKey(fromDate);
        }

        if (!wpMap.has(wpId)) {
            wpMap.set(wpId, { name: wpName, dayMap: new Map() });
        }
        const entry = wpMap.get(wpId)!;
        const dayData = entry.dayMap.get(key) || { okCount: 0, planCount: 0 };
        dayData.okCount += ok;
        dayData.planCount += plan;
        entry.dayMap.set(key, dayData);
    }

    return Array.from(wpMap.entries()).map(([workplaceId, data]) => ({
        workplaceId,
        workplaceName: data.name,
        points: Array.from(data.dayMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([day, dayData]) => ({
                day,
                fulfillmentPct: dayData.planCount > 0 ? (dayData.okCount / dayData.planCount) * 100 : null,
                pieceDiff: dayData.okCount - dayData.planCount,
                planCount: dayData.planCount,
                okCount: dayData.okCount,
            })),
    }));
}

/**
 * INTERNÍ FUNKCE - Výpočet plnění plánu podle projektů z již načtených dat
 */
function computePlanFulfillmentByProject(
    shifts: any[],
    granularity: 'day' | 'week' | 'month'
): PlanFulfillmentByProject[] {

    const projectMap = new Map<string, { name: string; dayMap: Map<string, { okCount: number; planCount: number }> }>();

    for (const s of shifts) {
        const productions = (s as any)?.shiftProductions || [];
        const totalOk = Number(s.okCount ?? 0) || 0;
        const totalPlan = Number(s.planCount ?? 0) || 0;

        // Pokud směna nemá productions, použijeme celkový okCount a planCount směny
        if (productions.length === 0) {
            if (totalOk > 0 || totalPlan > 0) {
                const projectId = 'noProject';
                const projectName = String((window as any)?.Vue?.$t?.('tpmAnalytics.noProject') || 'Bez projektu');

                let key: string;
                const m = moment(s.fromDate);
                if (granularity === 'week') {
                    key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
                } else if (granularity === 'month') {
                    key = m.clone().startOf('month').format('YYYY-MM-DD');
                } else {
                    const fromDate = s.fromDate ? new Date(s.fromDate).getTime() : Date.now();
                    key = productionDayKey(fromDate);
                }

                if (!projectMap.has(projectId)) {
                    projectMap.set(projectId, { name: projectName, dayMap: new Map() });
                }
                const entry = projectMap.get(projectId)!;
                if (!entry.dayMap.has(key)) {
                    entry.dayMap.set(key, { okCount: 0, planCount: 0 });
                }
                const dayEntry = entry.dayMap.get(key)!;
                dayEntry.okCount += totalOk;
                dayEntry.planCount += totalPlan;
            }
        } else {
            // Rozložíme okCount a planCount proporcionálně podle projektů
            const projectOks = productions.map((p: any) => Number(p?.okCount ?? 0) || 0);
            const sumProjectOks = projectOks.reduce((sum: number, v: number) => sum + v, 0);

            for (let i = 0; i < productions.length; i++) {
                const p = productions[i];
                const projectOk = projectOks[i];
                if (projectOk <= 0) continue;

                const projectId = p?.projectId || 'unknown';
                const projectName = (p as any)?.project?.name || projectId || 'N/A';

                // Proporcionální podíl z celkového plánu
                const proportion = sumProjectOks > 0 ? projectOk / sumProjectOks : 0;
                const projectPlan = totalPlan * proportion;

                let key: string;
                const m = moment(s.fromDate);
                if (granularity === 'week') {
                    key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
                } else if (granularity === 'month') {
                    key = m.clone().startOf('month').format('YYYY-MM-DD');
                } else {
                    const fromDate = s.fromDate ? new Date(s.fromDate).getTime() : Date.now();
                    key = productionDayKey(fromDate);
                }

                if (!projectMap.has(projectId)) {
                    projectMap.set(projectId, { name: projectName, dayMap: new Map() });
                }
                const entry = projectMap.get(projectId)!;
                if (!entry.dayMap.has(key)) {
                    entry.dayMap.set(key, { okCount: 0, planCount: 0 });
                }
                const dayEntry = entry.dayMap.get(key)!;
                dayEntry.okCount += projectOk;
                dayEntry.planCount += projectPlan;
            }
        }
    }

    return Array.from(projectMap.entries()).map(([projectId, data]) => ({
        projectId,
        projectName: data.name,
        points: Array.from(data.dayMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([day, d]) => ({
                day,
                fulfillmentPct: d.planCount > 0 ? (d.okCount / d.planCount) * 100 : null,
                pieceDiff: d.okCount - d.planCount,
                planCount: d.planCount,
                okCount: d.okCount,
            })),
    }));
}

/**
 * INTERNÍ FUNKCE - Výpočet plnění plánu podle směnových týmů z již načtených dat
 */
function computePlanFulfillmentByShiftTeam(
    shifts: any[],
    granularity: 'day' | 'week' | 'month'
): PlanFulfillmentByShiftTeam[] {

    const teamMap = new Map<string, { name: string; dayMap: Map<string, { okCount: number; planCount: number }> }>();

    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        const plan = Number(s.planCount ?? 0) || 0;
        if (ok <= 0 && plan <= 0) continue;

        const teamId = s.shiftTeamTypeId || 'noTeam';
        const teamName = s.shiftTeamTypeId 
            ? ((s as any)?.shiftTeamType?.name || s.shiftTeamTypeId)
            : String((window as any)?.Vue?.$t?.('tpmAnalytics.noShiftTeam') || 'Bez směnového týmu');

        let key: string;
        const m = moment(s.fromDate);
        if (granularity === 'week') {
            key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
        } else if (granularity === 'month') {
            key = m.clone().startOf('month').format('YYYY-MM-DD');
        } else {
            const fromDate = s.fromDate ? new Date(s.fromDate).getTime() : Date.now();
            key = productionDayKey(fromDate);
        }

        if (!teamMap.has(teamId)) {
            teamMap.set(teamId, { name: teamName, dayMap: new Map() });
        }
        const entry = teamMap.get(teamId)!;
        if (!entry.dayMap.has(key)) {
            entry.dayMap.set(key, { okCount: 0, planCount: 0 });
        }
        const dayEntry = entry.dayMap.get(key)!;
        dayEntry.okCount += ok;
        dayEntry.planCount += plan;
    }

    return Array.from(teamMap.entries()).map(([shiftTeamTypeId, data]) => ({
        shiftTeamTypeId,
        shiftTeamTypeName: data.name,
        points: Array.from(data.dayMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([day, d]) => ({
                day,
                fulfillmentPct: d.planCount > 0 ? (d.okCount / d.planCount) * 100 : null,
                pieceDiff: d.okCount - d.planCount,
                planCount: d.planCount,
                okCount: d.okCount,
            })),
    }));
}

/**
 * INTERNÍ FUNKCE - Výpočet plnění plánu podle typů směn z již načtených dat
 */
function computePlanFulfillmentByShiftType(
    shifts: any[],
    granularity: 'day' | 'week' | 'month'
): PlanFulfillmentByShiftType[] {

    const typeMap = new Map<string, { name: string; dayMap: Map<string, { okCount: number; planCount: number }> }>();

    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        const plan = Number(s.planCount ?? 0) || 0;
        if (ok <= 0 && plan <= 0) continue;

        const typeId = s.shiftTypeId || 'noType';
        const typeName = s.shiftTypeId 
            ? ((s as any)?.shiftType?.name || s.shiftTypeId)
            : String((window as any)?.Vue?.$t?.('tpmAnalytics.noShiftType') || 'Bez typu směny');

        let key: string;
        const m = moment(s.fromDate);
        if (granularity === 'week') {
            key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
        } else if (granularity === 'month') {
            key = m.clone().startOf('month').format('YYYY-MM-DD');
        } else {
            const fromDate = s.fromDate ? new Date(s.fromDate).getTime() : Date.now();
            key = productionDayKey(fromDate);
        }

        if (!typeMap.has(typeId)) {
            typeMap.set(typeId, { name: typeName, dayMap: new Map() });
        }
        const entry = typeMap.get(typeId)!;
        if (!entry.dayMap.has(key)) {
            entry.dayMap.set(key, { okCount: 0, planCount: 0 });
        }
        const dayEntry = entry.dayMap.get(key)!;
        dayEntry.okCount += ok;
        dayEntry.planCount += plan;
    }

    return Array.from(typeMap.entries()).map(([shiftTypeId, data]) => ({
        shiftTypeId,
        shiftTypeName: data.name,
        points: Array.from(data.dayMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([day, d]) => ({
                day,
                fulfillmentPct: d.planCount > 0 ? (d.okCount / d.planCount) * 100 : null,
                pieceDiff: d.okCount - d.planCount,
                planCount: d.planCount,
                okCount: d.okCount,
            })),
    }));
}

/**
 * INTERNÍ FUNKCE - Výpočet distribuce plnění z již načtených dat
 */
function computePlanFulfillmentDistribution(
    shifts: any[],
    from: moment.Moment,
    to: moment.Moment
): PlanFulfillmentDistribution {

    const ranges = [
        { label: '< 80%', min: 0, max: 80, count: 0 },
        { label: '80-90%', min: 80, max: 90, count: 0 },
        { label: '90-100%', min: 90, max: 100, count: 0 },
        { label: '100-110%', min: 100, max: 110, count: 0 },
        { label: '> 110%', min: 110, max: Infinity, count: 0 },
    ];

    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        const plan = Number(s.planCount ?? 0) || 0;
        if (plan <= 0) continue;

        const fulfillment = (ok / plan) * 100;
        for (const r of ranges) {
            if (fulfillment >= r.min && fulfillment < r.max) {
                r.count++;
                break;
            }
        }
    }

    const total = ranges.reduce((sum, r) => sum + r.count, 0);
    return {
        ranges: ranges.map((r) => ({
            label: r.label,
            count: r.count,
            pct: total > 0 ? (r.count / total) * 100 : 0,
        })),
    };
}

/**
 * INTERNÍ FUNKCE - Výpočet top 10 nejlepších/nejhorších výkonů z již načtených dat
 */
function computePlanFulfillmentTopItems(
    shifts: any[]
): { best: PlanFulfillmentTopItem[]; worst: PlanFulfillmentTopItem[] } {

    const items: PlanFulfillmentTopItem[] = shifts
        .filter((s: any) => {
            const plan = Number(s.planCount ?? 0) || 0;
            return plan > 0;
        })
        .map((s: any) => {
            const ok = Number(s.okCount ?? 0) || 0;
            const plan = Number(s.planCount ?? 0) || 0;
            return {
                workplaceName: (s as any)?.workplace?.name || s.workplaceId || 'N/A',
                date: moment.tz(s.fromDate, DISPLAY_TIMEZONE).format('DD.MM.YYYY'),
                planCount: plan,
                okCount: ok,
                fulfillmentPct: (ok / plan) * 100,
            };
        });

    const sorted = [...items].sort((a, b) => b.fulfillmentPct - a.fulfillmentPct);
    const best = sorted.slice(0, 10);
    const worst = sorted.slice(-10).reverse();

    return { best, worst };
}

// ============================================================================
// ZPĚTNÁ KOMPATIBILITA - Exportované funkce pro starý kód
// ============================================================================
// Tyto funkce zachovávají původní API, ale interně volají optimalizovanou
// getAllPlanFulfillmentAnalytics() aby se minimalizovaly GraphQL volání

/**
 * Shrnutí plnění plánu
 * @deprecated Použijte raději getAllPlanFulfillmentAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getPlanFulfillmentSummary(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<PlanFulfillmentSummary> {
    const from = moment(fromIso);
    const to = moment(toIso);
    const span = Math.max(1, to.diff(from, 'minutes'));
    const prevTo = from.clone();
    const prevFrom = from.clone().add(-span, 'minutes');

    const [shiftsNow, shiftsPrev] = await Promise.all([
        ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters),
        ShiftsRepository.fetchShiftsForRange([], prevFrom.toISOString(), prevTo.toISOString(), filters),
    ]);

    return computePlanFulfillmentSummary(shiftsNow, shiftsPrev, from, to);
}

/**
 * Trend plnění plánu
 * @deprecated Použijte raději getAllPlanFulfillmentAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getPlanFulfillmentTrend(
    fromIso: string,
    toIso: string,
    granularity: 'day' | 'week' | 'month',
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<PlanFulfillmentTrendPoint[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    return computePlanFulfillmentTrend(shifts, granularity);
}

/**
 * Plnění podle pracovišť
 * @deprecated Použijte raději getAllPlanFulfillmentAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getPlanFulfillmentByWorkplace(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<PlanFulfillmentByWorkplace[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    const from = moment(fromIso);
    const to = moment(toIso);
    return computePlanFulfillmentByWorkplace(shifts, from, to);
}

/**
 * Plnění podle projektů
 * @deprecated Použijte raději getAllPlanFulfillmentAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getPlanFulfillmentByProject(
    fromIso: string,
    toIso: string,
    granularity: 'day' | 'week' | 'month',
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<PlanFulfillmentByProject[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    return computePlanFulfillmentByProject(shifts, granularity);
}

/**
 * Plnění podle směnových týmů
 * @deprecated Použijte raději getAllPlanFulfillmentAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getPlanFulfillmentByShiftTeam(
    fromIso: string,
    toIso: string,
    granularity: 'day' | 'week' | 'month',
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<PlanFulfillmentByShiftTeam[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    return computePlanFulfillmentByShiftTeam(shifts, granularity);
}

/**
 * Plnění podle typů směn
 * @deprecated Použijte raději getAllPlanFulfillmentAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getPlanFulfillmentByShiftType(
    fromIso: string,
    toIso: string,
    granularity: 'day' | 'week' | 'month',
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<PlanFulfillmentByShiftType[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    return computePlanFulfillmentByShiftType(shifts, granularity);
}

/**
 * Distribuce plnění (histogram)
 * @deprecated Použijte raději getAllPlanFulfillmentAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getPlanFulfillmentDistribution(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<PlanFulfillmentDistribution> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    const from = moment(fromIso);
    const to = moment(toIso);
    return computePlanFulfillmentDistribution(shifts, from, to);
}

/**
 * Top 10 nejlepších/nejhorších výkonů
 * @deprecated Použijte raději getAllPlanFulfillmentAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getPlanFulfillmentTopItems(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<{ best: PlanFulfillmentTopItem[]; worst: PlanFulfillmentTopItem[] }> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    return computePlanFulfillmentTopItems(shifts);
}

